// How a quote reads on the board and in the CSV. Pure, so the tests can run
// every rule on fixed numbers.
//
// ⚠️ THE "s" IS A SETTLEMENT MARK, NOT DECORATION. Every quote vendor appends
// it to a price that is a written settlement rather than a trade still moving
// (barchart: 338.275s). Ours: the nightly close once it is written; the live
// reading carries no suffix. A reader who knows the convention reads it; one
// who does not loses nothing.

import type { RbIndexHistoryRow, RbIndexQuote } from "@/lib/rbIndexes";

const p2 = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const n0 = new Intl.NumberFormat("en-US");

/** 343.80 — never a dollar sign in a quote table; the unit is in the caption. */
export const px = (v: number | null | undefined): string => (v == null ? "—" : p2.format(v));
export const cnt = (v: number | null | undefined): string => (v == null ? "—" : n0.format(v));

/** +1.13 / −0.54 / 0.00 — an explicit sign, and a real minus, as a quote vendor prints it. */
export function signedPx(v: number | null | undefined): string {
  if (v == null) return "—";
  if (v > 0) return `+${p2.format(v)}`;
  if (v < 0) return `−${p2.format(-v)}`;
  return p2.format(0);
}
export function signedPct(v: number | null | undefined): string {
  if (v == null) return "—";
  const s = signedPx(v);
  return s === "—" ? s : `${s}%`;
}

/** +1,234 / −567 / 0 — a signed head count. */
export function signedCnt(v: number | null | undefined): string {
  if (v == null) return "—";
  if (v > 0) return `+${n0.format(v)}`;
  if (v < 0) return `−${n0.format(-v)}`;
  return "0";
}

/** The direction a change column is coloured. Flat is neither. */
export const dir = (v: number | null | undefined): "up" | "down" | "flat" | null =>
  v == null ? null : v > 0 ? "up" : v < 0 ? "down" : "flat";

/** 343.80s when settled; 343.80 while live. */
export const lastPx = (v: number | null | undefined, settled: boolean): string =>
  v == null ? "—" : `${p2.format(v)}${settled ? "s" : ""}`;

/** 10/06/26 — the vendor's date, two-digit year, as in the screenshot. */
/** The r2's one line: "Revised Oct 7, 11:54 PM: was 153.66, now 150.04. Pre-launch clean-up." */
export function revisionLine(revisedAt: string | null, before: number | null, after: number | null, reason: string | null): string {
  const when = revisedAt
    ? new Date(revisedAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" })
    : "later";
  const why = reason ? ` ${reason.charAt(0).toUpperCase()}${reason.slice(1)}.` : "";
  return `Revised ${when}: was ${px(before)}, now ${px(after)}.${why}`;
}

export function mdy(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[2]}/${m[3]}/${m[1].slice(2)}` : iso;
}

/* ── CSV ─────────────────────────────────────────────────────────────────── */

const cell = (v: unknown): string => {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const line = (vals: unknown[]): string => vals.map(cell).join(",");

/** The board as a vendor's download: raw figures, ISO dates, one header row. */
export function quotesCsv(rows: RbIndexQuote[]): string {
  const head = ["code", "index", "date", "last", "settled", "change", "change_pct", "open", "high", "low",
                "previous", "head", "lots", "barns", "states"];
  return [line(head), ...rows.map((q) => line([
    q.code, q.name, q.asOf, q.last, q.settled ? "s" : "", q.change, q.changePct, q.open, q.high, q.low,
    q.prev, q.head, q.lots, q.barns, q.states,
  ]))].join("\n") + "\n";
}

export function historyCsv(code: string, rows: RbIndexHistoryRow[]): string {
  const head = ["code", "date", "close", "change", "change_pct", "open", "high", "low", "step_minutes", "unknown_arrival_lots", "late_lots", "previous",
                "head", "head_week_ago", "head_change_week", "lots", "barns", "states", "revision"];
  return [line(head), ...rows.map((r) => line([
    code, r.asOf, r.close, r.change, r.changePct, r.open, r.high, r.low, r.stepMinutes, r.unknownArrivalLots, r.lateLots, r.prev,
    r.head, r.headWkAgo, r.headChgWk, r.lots, r.barns, r.states, r.revision,
  ]))].join("\n") + "\n";
}

/** Hands the browser a file. A plain data: link is enough here — this is the
 *  live site, not a sandbox — and revoking the URL after the click keeps the
 *  blob from outliving the page. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
